import { useCallback, useMemo, useState } from "react";
import { Header, Card } from "../molecules";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  PlusOutlined,
  SaveOutlined,
  EditOutlined,
  CloseOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import "../../resources/profile.scss";
import { useAppSelector } from "../../app/hooks";
import { setUserInfo, userState } from "../../store/slices/user";
import { Response } from "../../constants/api";
import { UserType } from "../../constants/user";
import { message } from "antd";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
  addTransaction,
  setTotals,
  transactionState,
} from "../../store/slices/transaction";
import {
  TransactionType,
  TransactionTypeEnum,
} from "../../constants/transaction";
import { formatCurrency } from "../../utils";
import { updateUserInfo } from "../../api/user";
import { createTransaction } from "../../api/transaction";
import { useNavigate } from "react-router-dom";

export const Profile = () => {
  const user = useAppSelector(userState);
  const navigate = useNavigate();
  const { transactions, income, expense, transfer } =
    useAppSelector(transactionState);

  const dispatch = useDispatch();

  const [profileDraft, setProfileDraft] = useState<UserType>({
    firstName: user.firstName,
    lastName: user.lastName,
    number: user.number,
    address: user.address,
  });

  const [isEditing, setIsEditing] = useState(false);

  const [formState, setFormState] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: "",
    category: "",
    type: TransactionTypeEnum.EXPENSE,
    amount: "",
  });

  const handleProfileEdit = () => {
    setIsEditing(true);
  };

  const handleProfileSave = useCallback(async () => {
    const res = await updateUserInfo(user?.userId, profileDraft);
    if (res?.success) {
      dispatch(setUserInfo({ ...res?.result }));
      setIsEditing(false);
      return;
    }

    message.error(res?.result || "Failed updating profile");
  }, [user, profileDraft, dispatch]);

  const handleProfileCancel = () => {
    setIsEditing(false);
  };

  const handleLogOut = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleTransactionChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTransaction = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    const amount = parseFloat(formState.amount);

    if (!formState.description || !formState.category || Number.isNaN(amount)) {
      return;
    }

    const newEntry: Omit<TransactionType, "_id" | "createdAt"> & {
      userId: string | null;
    } = {
      date: formState.date,
      description: formState.description,
      category: formState.category,
      type: formState.type,
      userId: user?.userId,
      amount,
    };

    const res: Response = await createTransaction(newEntry);
    if (res?.success) {
      console.log("here res", res);
      dispatch(addTransaction(res?.result));
      return;
    }

    message.error(res?.result || "Failed Creating Transaction");
  };

  useMemo(() => {
    const totals: any = {
      income: transactions
        .filter((item) => item.type === TransactionTypeEnum.INCOME)
        .reduce((sum, item) => sum + item.amount, 0),
      expense: transactions
        .filter((item) => item.type === TransactionTypeEnum.EXPENSE)
        .reduce((sum, item) => sum + item.amount, 0),
      transfer: transactions
        .filter((item) => item.type === TransactionTypeEnum.TRANSFER)
        .reduce((sum, item) => sum + item.amount, 0),
    };

    dispatch(setTotals({ ...totals }));
  }, [transactions, dispatch]);

  return (
    <div className="cp-profile">
      <Header />

      <div className="cp-profile__container">
        <div className="cp-profile__overview">
          <Card>
            <div className="cp-profile__card-header">
              <div>
                <p className="cp-profile__label">Profile details</p>
                <h2>Account profile</h2>
                <p className="cp-profile__subtitle">
                  {user?.description ||
                    "This is dummy profile data stored locally until your database is connected."}
                </p>
              </div>
              <button
                type="button"
                className="cp-profile__action-btn"
                onClick={isEditing ? handleProfileCancel : handleProfileEdit}
              >
                {isEditing ? (
                  <>
                    <CloseOutlined /> Cancel
                  </>
                ) : (
                  <>
                    <EditOutlined /> Edit
                  </>
                )}
              </button>
              <button
                type="button"
                className="cp-profile__action-btn"
                onClick={handleLogOut}
              >
                <LogoutOutlined /> Logout
              </button>
            </div>

            <div className="cp-profile__content">
              <div className="cp-profile__summary-card">
                <div className="cp-profile__avatar">
                  {user.firstName.charAt(0)}
                </div>
                <div>
                  <h3>{`${user.firstName} ${user.lastName}`}</h3>
                  <p>{user.email}</p>
                </div>
              </div>

              <div className="cp-profile__details">
                {isEditing ? (
                  <div className="cp-profile__edit-form">
                    <label>
                      First name
                      <input
                        name="firstName"
                        defaultValue={user.firstName}
                        onChange={(e) =>
                          setProfileDraft((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Email address
                      <input
                        name="email"
                        type="email"
                        disabled={true}
                        defaultValue={user.email}
                      />
                    </label>
                    <label>
                      Phone number
                      <input
                        name="number"
                        defaultValue={user.number}
                        onChange={(e) =>
                          setProfileDraft((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Address
                      <input
                        name="address"
                        defaultValue={user.address}
                        onChange={(e) =>
                          setProfileDraft((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="cp-profile__save-btn"
                      onClick={handleProfileSave}
                    >
                      <SaveOutlined /> Save changes
                    </button>
                  </div>
                ) : (
                  <div className="cp-profile__info-list">
                    <div>
                      <UserOutlined />{" "}
                      <span>{`${user.firstName} ${user.lastName}`}</span>
                    </div>
                    <div>
                      <MailOutlined /> <span>{user.email}</span>
                    </div>
                    <div>
                      <PhoneOutlined /> <span>{user.number}</span>
                    </div>
                    <div>
                      <EnvironmentOutlined /> <span>{user.address}</span>
                    </div>
                    <div>
                      <CalendarOutlined />{" "}
                      <span>
                        Member since{" "}
                        {moment(user.memberSince).format("MMM DD, YYYY")}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <div className="cp-profile__stats-grid">
              <div>
                <p>Total transactions</p>
                <h3>{transactions.length}</h3>
              </div>
              <div>
                <p>Income total</p>
                <h3>{formatCurrency(income)}</h3>
              </div>
              <div>
                <p>Expenses total</p>
                <h3>{formatCurrency(expense, true)}</h3>
              </div>
              <div>
                <p>Transfer total</p>
                <h3>{formatCurrency(transfer)}</h3>
              </div>
            </div>
          </Card>
        </div>

        <Card>
          <div className="cp-profile__form-header">
            <div>
              <p className="cp-profile__label">New transaction</p>
              <h3>Enter a new transaction</h3>
            </div>
            <div className="cp-profile__pill">
              <PlusOutlined /> Live entry
            </div>
          </div>

          <form
            className="cp-profile__transaction-form"
            onSubmit={handleAddTransaction}
          >
            <div className="cp-profile__form-grid">
              <label>
                Date
                <input
                  type="date"
                  name="date"
                  value={formState.date}
                  onChange={handleTransactionChange}
                />
              </label>
              <label>
                Description
                <input
                  type="text"
                  name="description"
                  value={formState.description}
                  onChange={handleTransactionChange}
                  placeholder="E.g. Grocery shopping"
                />
              </label>
              <label>
                Category
                <input
                  type="text"
                  name="category"
                  value={formState.category}
                  onChange={handleTransactionChange}
                  placeholder="E.g. Entertainment"
                />
              </label>
              <label>
                Amount
                <input
                  type="number"
                  name="amount"
                  value={formState.amount}
                  onChange={handleTransactionChange}
                  placeholder="E.g. 120.00"
                  step="0.01"
                />
              </label>
              <label>
                Type
                <select
                  name="type"
                  value={formState.type}
                  onChange={handleTransactionChange}
                >
                  <option value="Expense">Expense</option>
                  <option value="Income">Income</option>
                  <option value="Transfer">Transfer</option>
                </select>
              </label>
            </div>
            <button type="submit" className="cp-profile__submit-btn">
              Add transaction
            </button>
          </form>
        </Card>

        <Card>
          <div className="cp-profile__recent-header">
            <div>
              <p className="cp-profile__label">Recent entries</p>
              <h3>Latest transaction activity</h3>
            </div>
            <span>{transactions.length} entries</span>
          </div>

          <ul className="cp-profile__transaction-list">
            {transactions.map((transaction, index) => (
              <li key={`${transaction.type}-${index}`}>
                <div>
                  <p>{transaction.description}</p>
                  <span>{transaction.category}</span>
                </div>
                <div>
                  <strong>{formatCurrency(transaction.amount)}</strong>
                  <small>{transaction.date}</small>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
